/**
 * AI Analytics / Ticket Analysis Types
 *
 * NOTE: These are app-specific types for AI-powered ticket analysis.
 * There are no corresponding library types in @open/nexus-public-api.
 *
 * This module provides:
 * - Sentiment analysis (positive, negative, neutral, mixed)
 * - Category and priority suggestions with confidence scores
 * - Customer intent detection (question, complaint, request, etc.)
 * - Key topic extraction and tag suggestions
 */

// Ticket Analysis Types

export type SentimentLabel = 'positive' | 'neutral' | 'negative' | 'mixed'

export interface SentimentAspect {
  aspect: string
  sentiment: 'positive' | 'neutral' | 'negative'
  text: string
}

export interface Sentiment {
  label: SentimentLabel
  confidence: number
  aspects: SentimentAspect[]
}

export interface CategorySuggestion {
  category: string
  confidence: number
  reasoning: string
}

export type PriorityLevel = 'low' | 'medium' | 'high' | 'urgent'

export interface PrioritySuggestion {
  level: PriorityLevel
  confidence: number
  factors: string[]
}

export type CustomerIntent =
  | 'question'
  | 'complaint'
  | 'request'
  | 'feedback'
  | 'escalation'
  | 'cancellation'
  | 'technical-issue'

export interface TicketAnalysis {
  ticketId: string
  sentiment: Sentiment
  sentimentScore: number // -1 to 1
  categories: CategorySuggestion[]
  priority: PrioritySuggestion
  keyTopics: string[]
  customerIntent: CustomerIntent
  suggestedTags: string[]
  analyzedAt: string
}

// Utility functions
export function getSentimentColor(label: SentimentLabel): string {
  const colors: Record<SentimentLabel, string> = {
    positive: 'text-green-600',
    neutral: 'text-gray-600',
    negative: 'text-red-600',
    mixed: 'text-yellow-600',
  }
  return colors[label]
}

export function getSentimentBgColor(label: SentimentLabel): string {
  const colors: Record<SentimentLabel, string> = {
    positive: 'bg-green-100',
    neutral: 'bg-gray-100',
    negative: 'bg-red-100',
    mixed: 'bg-yellow-100',
  }
  return colors[label]
}

export function getPriorityColor(level: PriorityLevel): string {
  const colors: Record<PriorityLevel, string> = {
    low: 'text-gray-600',
    medium: 'text-blue-600',
    high: 'text-orange-600',
    urgent: 'text-red-600',
  }
  return colors[level]
}

export function getPriorityBgColor(level: PriorityLevel): string {
  const colors: Record<PriorityLevel, string> = {
    low: 'bg-gray-100',
    medium: 'bg-blue-100',
    high: 'bg-orange-100',
    urgent: 'bg-red-100',
  }
  return colors[level]
}

export function getIntentLabel(intent: CustomerIntent): string {
  const labels: Record<CustomerIntent, string> = {
    question: 'Question',
    complaint: 'Complaint',
    request: 'Request',
    feedback: 'Feedback',
    escalation: 'Escalation',
    cancellation: 'Cancellation',
    'technical-issue': 'Technical Issue',
  }
  return labels[intent]
}
